import {
  ListAlt,
  FilterAlt,
  CancelRounded,
  Save,
  Edit,
  Download,
} from '@mui/icons-material';
import {
  Button as MuiButton,
  Grid,
  Typography,
  IconButton,
} from '@mui/material';
import { map } from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VMasker from 'vanilla-masker';

import {
  PageWrapper,
  FiltersGrid,
  Input,
  DropdownInput,
  Button,
  Table,
  TableText,
  Accordion,
  Dropdown,
  DatePicker,
} from '../../components';

import { CREDENTIALS_CREATE, CREDENTIALS_UPDATE } from '../../routes/constants';
import { CredentialsListPageResponseType } from '../../services/Credentials/types';
import {
  useDispatch,
  useSelector,
  fetchCredentialsFilters,
  fetchCredentialsList,
  setCredentialsFiltersValues,
  resetCredentialsFiltersValues,
  setCredentialsListCurrentPage,
  clearCredentials,
} from '../../stores';
import { theme } from '../../theme';

import useResponsiveMode from '../../utils/useResponsiveMode';
import { CredentialsTableToolbar } from './CredentialsTableToolbar';
import { CredentialsExportModal } from './modals';
import { StyledFiltersBtnsGrid } from './styles';

export const CredentialsManagement: React.FC = () => {
  const responsiveMode = useResponsiveMode();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [isFilterAccordionExpanded, setIsFilterAccordionExpanded] =
    useState<boolean>(false);

  const filters = useSelector((state) => state.credentialsFiltersReducer);

  const credentialsFiltersValues = useSelector(
    (state) => state.credentialsFiltersValuesReducer,
  );

  const { currentPage, size, page, isListLoading } = useSelector(
    (state) => state.credentialsListSliceReducer,
  );

  useEffect(() => {
    dispatch(fetchCredentialsFilters());
  }, []);

  useEffect(() => {
    const params = {
      number: credentialsFiltersValues.number || undefined,
      licensePlate: credentialsFiltersValues.licensePlate || undefined,
      holderName: credentialsFiltersValues.holderName || undefined,
      holderDocument: credentialsFiltersValues.holderDocument || undefined,
      street: credentialsFiltersValues.street || undefined,
      district: credentialsFiltersValues.district || undefined,
      routeId: credentialsFiltersValues.routeId || undefined,
      expirationDate: credentialsFiltersValues.expirationDate || undefined,
      status:
        credentialsFiltersValues.status !== undefined &&
        credentialsFiltersValues.status !== ''
          ? credentialsFiltersValues.status
          : undefined,
    };

    dispatch(fetchCredentialsList(params));
  }, [currentPage]);

  const handleFilter = () => {
    dispatch(setCredentialsListCurrentPage(1));

    const params = {
      number: credentialsFiltersValues.number || undefined,
      licensePlate: credentialsFiltersValues.licensePlate || undefined,
      holderName: credentialsFiltersValues.holderName || undefined,
      holderDocument: credentialsFiltersValues.holderDocument || undefined,
      street: credentialsFiltersValues.street || undefined,
      district: credentialsFiltersValues.district || undefined,
      routeId: credentialsFiltersValues.routeId || undefined,
      expirationDate: credentialsFiltersValues.expirationDate || undefined,
      status:
        credentialsFiltersValues.status !== undefined &&
        credentialsFiltersValues.status !== ''
          ? credentialsFiltersValues.status
          : undefined,
    };

    dispatch(fetchCredentialsList(params));
  };

  const handleClearFilter = () => {
    dispatch(resetCredentialsFiltersValues());
  };

  const handleEdit = (id: number) => {
    dispatch(clearCredentials());
    navigate(`${CREDENTIALS_UPDATE}/${id}`);
  };

  const columns = [
    {
      field: 'number',
      headerName: 'Número',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.number.toString()} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'licensePlate',
      headerName: 'Placa',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.licensePlate} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'holderName',
      headerName: 'Nome',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.holderName} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'street',
      headerName: 'Logradouro',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.street} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'district',
      headerName: 'Bairro',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.district} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'routes',
      headerName: 'Trechos',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.routes || ''} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'expirationDate',
      headerName: 'Data de validade',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText
          label={moment(params.row.expirationDate).format('DD/MM/YYYY')}
        />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'actions',
      headerName: 'Ações',
      editable: false,
      sortable: false,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <MuiButton
          variant='contained'
          startIcon={<Edit />}
          size='small'
          disabled={params.row.status === 0}
          onClick={() => handleEdit(params.row.id)}
          sx={{ paddingLeft: 1, paddingRight: 1 }}
        >
          Editar
        </MuiButton>
      ),
      headerClassName: 'super-app-theme--header',
    },
  ];

  const columnsResponsive = [
    {
      field: 'number',
      headerName: 'Número',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'licensePlate',
      headerName: 'Placa',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'holderName',
      headerName: 'Nome',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'street',
      headerName: 'Logradouro',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'district',
      headerName: 'Bairro',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'routes',
      headerName: 'Trechos',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <TableText label={params.row.routes || ''} />
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'expirationDate',
      headerName: 'Data de validade',
      editable: false,
      sortable: false,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'actions',
      headerName: 'Ações',
      editable: false,
      sortable: false,
      renderCell: (params: { row: CredentialsListPageResponseType }) => (
        <MuiButton
          variant='contained'
          size='small'
          disabled={params.row.status === 0}
          onClick={() => handleEdit(params.row.id)}
        >
          <Edit />
        </MuiButton>
      ),
      headerClassName: 'super-app-theme--header',
    },
  ];

  return (
    <PageWrapper>
      <Grid container>
        <Grid item lg={12} md={12} sm={12} xs={12} margin={1}>
          <Typography
            variant={responsiveMode !== 'phone' ? 'h4' : 'h5'}
            margin={1}
            display='flex'
            justifyContent='flex-start'
            alignItems='center'
            color={theme.palette.text.main}
          >
            <ListAlt fontSize='large' sx={{ marginRight: 2 }} />
            Gerenciar credenciais
          </Typography>
        </Grid>
      </Grid>
      {responsiveMode !== 'phone' ? (
        <Grid container>
          <FiltersGrid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='Número'
                value={credentialsFiltersValues.number?.toString() || ''}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      number: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='Placa'
                value={credentialsFiltersValues.licensePlate || ''}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      licensePlate: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='Nome do credenciado'
                value={credentialsFiltersValues.holderName || ''}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      holderName: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='CPF do credenciado'
                value={VMasker.toPattern(
                  credentialsFiltersValues.holderDocument || '',
                  '999.999.999-99',
                )}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      holderDocument: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='Logradouro'
                value={credentialsFiltersValues.street || ''}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      street: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
              <Input
                type='text'
                margin='normal'
                label='Bairro'
                value={credentialsFiltersValues.district || ''}
                onChange={(value) =>
                  dispatch(
                    setCredentialsFiltersValues({
                      ...credentialsFiltersValues,
                      district: value,
                    }),
                  )
                }
              />
            </Grid>
            <Grid
              container
              xs={12}
              margin={1}
              justifyContent='center'
              alignItems='center'
            >
              <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
                <DropdownInput
                  label='Trechos'
                  options={filters.filters ? filters.filters.routes : []}
                  value={credentialsFiltersValues.routeId || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        routeId: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
                <DatePicker
                  label='Valido até'
                  value={credentialsFiltersValues.expirationDate || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        expirationDate: value,
                      }),
                    )
                  }
                />
              </Grid>

              <Grid item lg={1.5} md={2} sm={4} xs={12} margin={1}>
                <Dropdown
                  label='Situação'
                  options={{
                    '': 'Todos',
                    1: 'Ativada',
                    2: 'Desativada',
                  }}
                  value={
                    credentialsFiltersValues.status === 1 ||
                    credentialsFiltersValues.status === 2
                      ? String(credentialsFiltersValues.status)
                      : ''
                  }
                  onChange={(value) => {
                    let statusValue: '' | 1 | 2;
                    if (value === '') {
                      statusValue = '';
                    } else if (value === '1') {
                      statusValue = 1;
                    } else if (value === '2') {
                      statusValue = 2;
                    } else {
                      statusValue = '';
                    }
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        status: statusValue,
                      }),
                    );
                  }}
                />
              </Grid>
              <StyledFiltersBtnsGrid>
                <Button
                  variant='contained'
                  label='Filtrar'
                  startIcon={<FilterAlt />}
                  onClick={handleFilter}
                  disabled={isListLoading}
                />
                <IconButton
                  color='secondary'
                  aria-label='Remover filtros'
                  onClick={handleClearFilter}
                  disabled={isListLoading}
                >
                  <CancelRounded fontSize='large' />
                </IconButton>
              </StyledFiltersBtnsGrid>
            </Grid>
          </FiltersGrid>
        </Grid>
      ) : (
        <Accordion
          title='Filtros'
          expanded={isFilterAccordionExpanded}
          setExpanded={setIsFilterAccordionExpanded}
        >
          <Grid container>
            <FiltersGrid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Número'
                  value={credentialsFiltersValues.number?.toString() || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        number: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Placa'
                  value={credentialsFiltersValues.licensePlate || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        licensePlate: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Nome do credenciado'
                  value={credentialsFiltersValues.holderName || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        holderName: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='CPF do credenciado'
                  value={VMasker.toPattern(
                    credentialsFiltersValues.holderDocument || '',
                    '999.999.999-99',
                  )}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        holderDocument: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Logradouro'
                  value={credentialsFiltersValues.street || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        street: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Bairro'
                  value={credentialsFiltersValues.district || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        district: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <DropdownInput
                  label='Trechos'
                  options={filters.filters ? filters.filters.routes : []}
                  value={credentialsFiltersValues.routeId || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        routeId: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <DatePicker
                  label='Valido até'
                  value={credentialsFiltersValues.expirationDate || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        expirationDate: value,
                      }),
                    )
                  }
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <DropdownInput
                  label='Situação'
                  options={[
                    { id: '', value: 'Todos' },
                    { id: 1, value: 'Ativada' },
                    { id: 2, value: 'Desativada' },
                  ]}
                  value={
                    credentialsFiltersValues.status === 1 ||
                    credentialsFiltersValues.status === 2
                      ? String(credentialsFiltersValues.status)
                      : ''
                  }
                  onChange={(value) => {
                    let statusValue: '' | 1 | 2;
                    if (value === '') {
                      statusValue = '';
                    } else if (value === '1') {
                      statusValue = 1;
                    } else if (value === '2') {
                      statusValue = 2;
                    } else {
                      statusValue = '';
                    }
                    dispatch(
                      setCredentialsFiltersValues({
                        ...credentialsFiltersValues,
                        status: statusValue,
                      }),
                    );
                  }}
                />
              </Grid>
              <StyledFiltersBtnsGrid>
                <Button
                  variant='contained'
                  label='Filtrar'
                  startIcon={<FilterAlt />}
                  onClick={handleFilter}
                  disabled={isListLoading}
                />
                <IconButton
                  color='secondary'
                  aria-label='Remover filtros'
                  onClick={handleClearFilter}
                  disabled={isListLoading}
                >
                  <CancelRounded fontSize='large' />
                </IconButton>
              </StyledFiltersBtnsGrid>
            </FiltersGrid>
          </Grid>
        </Accordion>
      )}
      <Grid container marginTop={5} marginBottom={5}>
        <Table
          loading={isListLoading}
          changePage={(page: number) =>
            dispatch(setCredentialsListCurrentPage(page))
          }
          page={currentPage}
          fetchRows={() => {
            const params = {
              number: credentialsFiltersValues.number || undefined,
              licensePlate: credentialsFiltersValues.licensePlate || undefined,
              holderName: credentialsFiltersValues.holderName || undefined,
              holderDocument:
                credentialsFiltersValues.holderDocument || undefined,
              street: credentialsFiltersValues.street || undefined,
              district: credentialsFiltersValues.district || undefined,
              routeId: credentialsFiltersValues.routeId || undefined,
              expirationDate:
                credentialsFiltersValues.expirationDate || undefined,
              status:
                credentialsFiltersValues.status !== undefined &&
                credentialsFiltersValues.status !== ''
                  ? credentialsFiltersValues.status
                  : undefined,
            };

            dispatch(fetchCredentialsList(params));
          }}
          rowCount={size}
          rows={page}
          columns={
            responsiveMode === 'phone' || responsiveMode === 'tablet'
              ? columnsResponsive
              : columns
          }
          toolbar={<CredentialsTableToolbar />}
        />
      </Grid>
      <CredentialsExportModal />
    </PageWrapper>
  );
};
